import React, { useState } from "react";
import {
  Typography,
  Table,
  Button,
  Modal,
  message,
  Spin,
  Card,
  Row,
  Col,
  Popconfirm,
  Form,
  Tooltip,
  Input
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  DatabaseOutlined,
  CloudServerOutlined
} from "@ant-design/icons";
import UserModal from "../components/UserModal";
import AssignResourcesModal from "../components/AssignResourcesModal";
import { permissions } from '../utils/permissions';
import useUsers from '../hooks/useUsers';
import useTableSearchPagination from '../hooks/useTableSearchPagination';

const { Title, Paragraph } = Typography;
const { confirm: ConfirmModal } = Modal;

export default function UsersView() {
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningUser, setAssigningUser] = useState(null);
  const [mode, setMode] = useState("create");
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [initialTab, setInitialTab] = useState("devices");

  const { users, loading, error, reload, saveUser, deleteUser } = useUsers();

  const openCreateModal = () => {
    setMode("create");
    setSelectedUser(null);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setMode("edit");
    setSelectedUser(record);
    setModalOpen(true);
  };

  const handleSave = async (values, form) => {
    setSaving(true);
    const result = await saveUser(mode, selectedUser?.username, values);

    if (result.success) {
      message.success(result.message);
      form.resetFields();
      setModalOpen(false);
      reload();
    } else {
      message.error(result.message);
    }

    setSaving(false);
  };

  const handleDelete = (record) => {
    ConfirmModal({
      title: "Are you sure you want to delete this user?",
      icon: <ExclamationCircleOutlined />,
      content: `${record.username} will be permanently removed.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const result = await deleteUser(record.username);
        if (result.success) {
          message.success(result.message);
          reload();
        } else {
          message.error(result.message);
        }
      },
    });
  };

  const columns = [
    { title: "Username", dataIndex: "username", key: "username" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Roles",
      dataIndex: "roles",
      key: "roles",
      render: roles => roles?.map(role => role.description).join(", ") || "-"
    },
    {
      title: "Link/Unlink",
      key: "linkunlink",
      render: (_, record) => {
        const roles = record.roles || [];

        return (
          <>
            <Tooltip title="Devices">
              <Button
                icon={<DatabaseOutlined />}
                size="small"
                style={{ marginRight: 8 }}
                onClick={() => {
                  setAssigningUser(record);
                  setInitialTab("devices");
                  setAssignModalOpen(true);
                }}
              />
            </Tooltip>
            <Tooltip title="Emission points">
              <Button
                icon={<CloudServerOutlined />}
                size="small"
                style={{ marginRight: 8 }}
                onClick={() => {
                  setAssigningUser(record);
                  setInitialTab("emissionPoints");
                  setAssignModalOpen(true);
                }}
              />
            </Tooltip>

          </>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => openEditModal(record)}
              style={{ marginRight: 8 }}
            />
          </Tooltip>

          <Popconfirm
            title="Delete user?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </>
      ),
    }
  ];

  const filterUser = (u, searchText) => {
    const q = searchText.toLowerCase();

    return (
      (u.username || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.roles || [])
        .map(r => r.description)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  };

  const {
    searchText,
    onSearchChange,
    filteredData: filteredUsers,
    pagination
  } = useTableSearchPagination({
    data: users,
    filterFn: filterUser
  });

  return (
    <div style={{ padding: 24 }}>
      <Row style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Users</Title>
        </Col>
      </Row>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Input
            placeholder="Search users..."
            value={searchText}
            onChange={e => onSearchChange(e.target.value)}
            allowClear
            style={{ width: 260 }}
          />
        </Col>

        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            style={{ backgroundColor: "#0075C9" }}
          >
            Create User
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin size="large" />
        </div>
      ) : !users.length ? (
        <Paragraph style={{ marginTop: 40 }}>No users found.</Paragraph>
      ) : (
        <Card>
         <Table
            rowKey="username"
            dataSource={filteredUsers}
            columns={columns}
            size="middle"
            pagination={pagination}
          />
        </Card>
      )}

      <UserModal
        open={modalOpen}
        mode={mode}
        form={form}
        selectedUser={selectedUser}
        saving={saving}
        onCancel={() => setModalOpen(false)}
        onSave={handleSave}
      />

      <AssignResourcesModal
        open={assignModalOpen}
        user={assigningUser}
        initialTab={initialTab}
        onClose={() => {
          setAssignModalOpen(false);
          setAssigningUser(null);
          // reload();
        }}
      />
    </div>
  );
}
